import { useMemo } from "react";
import { instantMeiliSearch } from "@meilisearch/instant-meilisearch";
import {
  InstantSearch,
  SearchBox,
  Hits,
  Pagination,
  Stats,
  Configure,
  Highlight,
  SortBy,
  useRefinementList,
} from "react-instantsearch";
import "instantsearch.css/themes/satellite-min.css";
import { InstantSearchInputIcon, InstantSearchResultsLoading } from "@/components/SearchLoading";
import NewBadge from "@/components/NewBadge";
import { ExternalLink } from "lucide-react";
import { ecuTypeLabel, vehicleTypeLabel } from "@/lib/labels";
import { useBrandLogos, brandLogoFor } from "@/lib/brandLogos";
import VehicleTypeIcon from "@/components/VehicleTypeIcon";

/**
 * Portable Meilisearch InstantSearch UI for the `toolscraper_compatibility`
 * index. Self-contained: only plain Tailwind classes (no dependency on any
 * host project's custom theme tokens), so it should look right dropped into
 * any Tailwind-enabled React app as-is.
 *
 * Required npm deps in the consuming project:
 *   react-instantsearch  @meilisearch/instant-meilisearch  meilisearch
 *   instantsearch.css    lucide-react
 *
 * Meilisearch is plain HTTP-only. If the host page is served over HTTPS,
 * point `meiliHost` at a same-origin backend route that proxies to
 * Meilisearch server-side (browsers block HTTP calls from an HTTPS page as
 * mixed content) — see backend/server.py's `/api/meili/{path}` in the
 * toolscraper repo for a ~30-line FastAPI example to copy.
 *
 * Props:
 * - meiliHost        (required) e.g. "https://yourapp.com/api/meili"
 * - meiliSearchKey    a scoped SEARCH-ONLY key. Safe to ship client-side.
 *                      Omit if your proxy injects the key server-side.
 * - indexName         default "toolscraper_compatibility"
 * - hitsPerPage       default 20
 * - getResultHref(hit) => string | undefined — link target per row.
 *                      Omit to render rows as non-clickable.
 * - LinkComponent      optional router Link component (e.g. react-router's
 *                      `Link`, expects a `to` prop) for client-side
 *                      navigation. Omit to render plain `<a href>` tags
 *                      (full page load) — the safe default for any host app.
 * - renderMethodIcon(label) => ReactNode | null — optional icon per
 *                      method chip (e.g. your own MethodGlyph). Falls back
 *                      to the plain text label.
 * - renderEcuLogo(ecuManufacturerOrName) => ReactNode | null — optional
 *                      brand logo next to ECU name / in the facet list.
 * - className          extra classes on the root wrapper.
 */
export default function CompatibilitySearch({
  meiliHost,
  meiliSearchKey = "unused",
  indexName = "toolscraper_compatibility",
  hitsPerPage = 20,
  getResultHref,
  renderMethodIcon,
  renderEcuLogo,
  LinkComponent,
  className = "",
}) {
  if (!meiliHost) {
    throw new Error("CompatibilitySearch: meiliHost prop is required");
  }

  const searchClient = useMemo(
    () => instantMeiliSearch(meiliHost, meiliSearchKey).searchClient,
    [meiliHost, meiliSearchKey],
  );
  const brandLogos = useBrandLogos();

  return (
    <div className={`text-neutral-100 ${className}`}>
      <InstantSearch searchClient={searchClient} indexName={indexName}>
        <Configure
          hitsPerPage={hitsPerPage}
          attributesToHighlight={[
            "manufacturer",
            "model",
            "version",
            "engine",
            "ecu_name",
            "protocol_label",
          ]}
        />

        <div className="rounded-xl border border-neutral-800 bg-panel p-4">
          <div className="relative flex-1 min-w-[240px]">
            <InstantSearchInputIcon />
            <SearchBox
              placeholder="Suche Hersteller, Modell, Motor, ECU/TCU, Protokoll…"
              classNames={{
                root: "w-full",
                form: "relative",
                input:
                  "appearance-none [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden flex h-9 w-full rounded-md border border-neutral-800 bg-panel px-3 py-1 pl-9 text-base shadow-sm transition-colors placeholder:text-neutral-500 focus:outline-none focus:ring-1 focus:ring-red-500 md:text-sm text-neutral-100",
                submitIcon: "hidden",
                resetIcon: "hidden",
                loadingIndicator: "hidden",
              }}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 mt-6">
          <aside className="space-y-5">
            <RefinementFacet
              title="Hersteller"
              attribute="manufacturer"
              sortBy={["name:asc"]}
              renderIcon={(name) => renderBrandFilterLogo(brandLogos, name)}
            />
            <RefinementFacet
              title="Fahrzeugtyp"
              attribute="vehicle_type"
              labelFor={vehicleTypeLabel}
              renderIcon={renderVehicleTypeIcon}
            />
            <RefinementFacet
              title="ECU / TCU"
              attribute="ecu_type"
              labelFor={ecuTypeLabel}
            />
            <RefinementFacet
              title="ECU-Hersteller"
              attribute="ecu_manufacturer"
              renderIcon={renderEcuLogo}
            />
            <RefinementFacet
              title="Methoden"
              attribute="methods"
              renderIcon={renderMethodIcon}
            />
          </aside>

          <div className="space-y-4 min-w-0">
            <div className="flex items-center justify-between gap-4">
              <div className="text-xs text-neutral-500">
                <Stats
                  classNames={{ root: "inline" }}
                  translations={{
                    rootElementText: ({ nbHits, processingTimeMS }) =>
                      `${nbHits.toLocaleString("de-DE")} Treffer in ${processingTimeMS}ms`,
                  }}
                />
              </div>
              <SortBy
                items={[
                  { value: indexName, label: "Relevanz" },
                  {
                    value: `${indexName}:year:desc`,
                    label: "Baujahr (neueste zuerst)",
                  },
                  {
                    value: `${indexName}:year:asc`,
                    label: "Baujahr (älteste zuerst)",
                  },
                ]}
                classNames={{
                  root: "shrink-0",
                  select:
                    "h-9 rounded-md border border-neutral-800 bg-panel text-neutral-100 text-sm px-2.5 focus:outline-none focus:ring-2 focus:ring-[#D32222]",
                }}
              />
            </div>

            <InstantSearchResultsLoading>
              <Hits
                classNames={{
                  root: "",
                  list: "divide-y divide-neutral-800/70 border border-neutral-800/70 rounded-xl overflow-hidden bg-panel",
                  item: "",
                }}
                hitComponent={(props) => (
                  <Hit
                    {...props}
                    getResultHref={getResultHref}
                    renderMethodIcon={renderMethodIcon}
                    renderEcuLogo={renderEcuLogo}
                    LinkComponent={LinkComponent}
                  />
                )}
              />
            </InstantSearchResultsLoading>

            <Pagination
              classNames={{
                root: "flex justify-center pt-2",
                list: "flex items-center gap-1",
                item: "px-3 py-1.5 rounded-md text-sm border border-neutral-800 bg-panel text-neutral-300 hover:text-neutral-50 hover:border-neutral-700 transition",
                selectedItem: "!border-red-500/60 !bg-red-500/15 !text-red-400",
                disabledItem: "opacity-40 pointer-events-none",
                link: "block",
              }}
            />
          </div>
        </div>
      </InstantSearch>
    </div>
  );
}

function renderBrandFilterLogo(brandLogos, name) {
  const logo = brandLogoFor(brandLogos, name);
  return (
    logo && (
      <span className="w-7 h-7 flex items-center justify-center shrink-0">
        <img src={logo} alt="" className="max-h-6 max-w-6 object-contain" loading="lazy" />
      </span>
    )
  );
}

function renderVehicleTypeIcon(type) {
  return <VehicleTypeIcon type={type} className="h-5 w-7" />;
}

function RefinementFacet({ title, attribute, renderIcon, labelFor, sortBy = ["count:desc"] }) {
  const { items, refine, canToggleShowMore, isShowingMore, toggleShowMore } =
    useRefinementList({
      attribute,
      limit: 8,
      showMore: true,
      showMoreLimit: 40,
      sortBy,
    });

  if (items.length === 0) return null;

  return (
    <div className="rounded-xl border border-neutral-800 bg-panel p-4">
      <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-3">
        {title}
      </div>
      <ul className="space-y-1.5">
        {items.map((item) => (
          <li key={item.label} className="flex items-center">
            <label className="flex items-center gap-2 text-sm text-neutral-300 hover:text-neutral-100 cursor-pointer w-full">
              <input
                type="checkbox"
                checked={item.isRefined}
                onChange={() => refine(item.value)}
                className="accent-red-500 w-3.5 h-3.5 rounded border-neutral-700 bg-panel"
              />
              {renderIcon?.(item.label)}
              <span className="flex-1 truncate">
                {labelFor ? labelFor(item.label) : item.label}
              </span>
              <span className="text-[10px] font-mono text-neutral-500 bg-panel border border-neutral-800 rounded px-1.5 py-0.5">
                {item.count}
              </span>
            </label>
          </li>
        ))}
      </ul>
      {canToggleShowMore && (
        <button
          type="button"
          onClick={toggleShowMore}
          className="mt-2 text-xs text-red-400 hover:text-red-300 transition"
        >
          {isShowingMore ? "Weniger" : "Mehr"}
        </button>
      )}
    </div>
  );
}

const highlightClassNames = {
  highlighted: "bg-red-500/30 text-red-400 rounded-sm px-0.5",
};

function Hit({ hit, getResultHref, renderMethodIcon, renderEcuLogo, LinkComponent }) {
  const href = getResultHref?.(hit);
  const Wrapper = href ? LinkComponent || "a" : "div";
  const wrapperProps = href ? { [LinkComponent ? "to" : "href"]: href } : {};

  return (
    <Wrapper
      {...wrapperProps}
      className={`group grid grid-cols-1 md:grid-cols-12 gap-4 px-4 py-4 w-full transition ${href ? "hover:bg-neutral-800/50 cursor-pointer" : ""}`}
    >
      <div className="md:col-span-3 flex items-center gap-3">
        {hit.manufacturer_logo && (
          <img
            src={hit.manufacturer_logo}
            alt={hit.manufacturer}
            className="h-12 w-12 object-contain rounded bg-neutral-900 border border-neutral-800 p-1 shrink-0"
            loading="lazy"
          />
        )}
        <div>
          <div className="font-medium text-neutral-100">
            <Highlight attribute="manufacturer" hit={hit} classNames={highlightClassNames} />
          </div>
          <div className="text-[11px] uppercase tracking-widest text-neutral-500">
            <VehicleTypeIcon type={hit.vehicle_type} className="inline-block h-4 w-5 mr-1 align-[-2px]" />
            {vehicleTypeLabel(hit.vehicle_type)}
          </div>
        </div>
      </div>
      <div className="md:col-span-2">
        <div className="text-neutral-100">
          {hit.model ? (
            <Highlight attribute="model" hit={hit} classNames={highlightClassNames} />
          ) : (
            "—"
          )}
        </div>
        <div className="text-xs text-neutral-500">{hit.year}</div>
        <NewBadge firstSeen={hit.first_seen} className="mt-1" />
      </div>
      <div className="md:col-span-3 text-neutral-200">
        {hit.engine ? (
          <Highlight attribute="engine" hit={hit} classNames={highlightClassNames} />
        ) : (
          "—"
        )}
        {hit.fuel && (
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mt-0.5">
            {hit.fuel}
          </div>
        )}
      </div>
      <div className="md:col-span-2">
        <div className="text-neutral-100 flex items-center gap-1.5">
          {renderEcuLogo?.(hit.ecu_manufacturer)}
          <Highlight attribute="ecu_name" hit={hit} classNames={highlightClassNames} />
          {href && (
            <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition" />
          )}
        </div>
        <div
          className="text-[11px] text-neutral-400 font-mono mt-0.5"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          <Highlight attribute="protocol_label" hit={hit} classNames={highlightClassNames} />
        </div>
      </div>
      <div className="md:col-span-2 flex flex-wrap gap-1.5 items-center">
        {(hit.methods || []).map((label, i) => (
          <span key={i} title={label} className="inline-flex items-center">
            {renderMethodIcon?.(label) || (
              <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-400 border border-neutral-700 rounded px-1.5 py-0.5">
                {label}
              </span>
            )}
          </span>
        ))}
      </div>
    </Wrapper>
  );
}
